import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBook, FiClock, FiUsers, FiTarget, FiTrendingUp, FiPlay, FiCheckCircle } = FiIcons;

const Courses = () => {
  const [selectedCourse, setSelectedCourse] = useState(null);

  const courses = [
    {
      id: 1,
      title: 'Marketing Fundamentals',
      code: 'MKT 101',
      description: 'Introduction to marketing principles, consumer behavior, and market analysis',
      duration: '16 weeks',
      students: 45,
      level: 'Beginner',
      color: 'blue',
      modules: [
        'Introduction to Marketing',
        'Consumer Behavior',
        'Market Research',
        'Product Development',
        'Pricing Strategies',
        'Distribution Channels',
        'Promotion & Advertising',
        'Digital Marketing Basics'
      ],
      assignments: [
        'Market Analysis Report',
        'Consumer Survey Project',
        'Brand Strategy Presentation',
        'Marketing Campaign Design'
      ]
    },
    {
      id: 2,
      title: 'Sports Marketing',
      code: 'MKT 301',
      description: 'Advanced course covering sports marketing strategies, sponsorship, and fan engagement',
      duration: '16 weeks',
      students: 32,
      level: 'Advanced',
      color: 'green',
      modules: [
        'Sports Industry Overview',
        'Sponsorship Strategies',
        'Event Marketing',
        'Athlete Endorsements',
        'Fan Engagement',
        'Digital Sports Marketing',
        'Sports Media Relations',
        'Revenue Generation'
      ],
      assignments: [
        'Sponsorship Proposal',
        'Event Marketing Plan',
        'Social Media Campaign',
        'ROI Analysis Project'
      ]
    },
    {
      id: 3,
      title: 'Digital Marketing Strategy',
      code: 'MKT 201',
      description: 'Comprehensive digital marketing course covering SEO, social media, and analytics',
      duration: '12 weeks',
      students: 38,
      level: 'Intermediate',
      color: 'purple',
      modules: [
        'Digital Marketing Landscape',
        'SEO & SEM',
        'Social Media Marketing',
        'Content Marketing',
        'Email Marketing',
        'Analytics & Metrics',
        'Marketing Automation',
        'E-commerce Marketing'
      ],
      assignments: [
        'SEO Audit Report',
        'Social Media Strategy',
        'Content Calendar',
        'Analytics Dashboard'
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      green: 'bg-green-100 text-green-800 border-green-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200'
    };
    return colors[color] || colors.blue;
  };

  const getGradientClasses = (color) => {
    const gradients = {
      blue: 'from-blue-50 to-blue-100',
      green: 'from-green-50 to-green-100',
      purple: 'from-purple-50 to-purple-100'
    };
    return gradients[color] || gradients.blue;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Course Catalog</h1>
        <p className="text-gray-600">Explore our comprehensive marketing and sports marketing curriculum</p>
      </motion.div>

      {/* Courses Grid */}
      <div className="grid lg:grid-cols-3 gap-8 mb-12">
        {courses.map((course, index) => (
          <motion.div
            key={course.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className={`bg-gradient-to-br ${getGradientClasses(course.color)} rounded-lg p-6 cursor-pointer hover:shadow-lg transition-shadow border border-gray-200`}
            onClick={() => setSelectedCourse(selectedCourse === course.id ? null : course.id)}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiBook} className={`text-${course.color}-600 text-xl`} />
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getColorClasses(course.color)}`}>
                {course.level}
              </span>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{course.title}</h3>
            <p className="text-sm text-gray-600 mb-1">{course.code}</p>
            <p className="text-gray-700 mb-4">{course.description}</p>
            
            <div className="flex items-center justify-between text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiClock} />
                <span>{course.duration}</span>
              </div>
              <div className="flex items-center space-x-1">
                <SafeIcon icon={FiUsers} />
                <span>{course.students} students</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Course Details */}
      {selectedCourse && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-lg shadow-lg p-8"
        >
          {(() => {
            const course = courses.find(c => c.id === selectedCourse);
            return (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{course.title}</h2>
                    <p className="text-gray-600">{course.code}</p>
                  </div>
                  <button
                    onClick={() => setSelectedCourse(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <SafeIcon icon={FiTarget} className="text-xl" />
                  </button>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Modules</h3>
                    <div className="space-y-2">
                      {course.modules.map((module, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                          <span className="text-gray-700">{module}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Assignments</h3>
                    <div className="space-y-2">
                      {course.assignments.map((assignment, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <SafeIcon icon={FiTarget} className="text-blue-500" />
                          <span className="text-gray-700">{assignment}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Course Information</h4>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Duration:</span>
                      <span className="ml-2 font-medium">{course.duration}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Enrolled:</span>
                      <span className="ml-2 font-medium">{course.students} students</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Level:</span>
                      <span className="ml-2 font-medium">{course.level}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </motion.div>
      )}

      {/* Learning Outcomes */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-16 bg-white rounded-lg shadow-md p-8"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Learning Outcomes</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-3">Marketing Skills</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• Strategic thinking and planning</li>
              <li>• Consumer behavior analysis</li>
              <li>• Brand management and positioning</li>
              <li>• Digital marketing proficiency</li>
              <li>• Data-driven decision making</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-3">Professional Development</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• Critical thinking and problem-solving</li>
              <li>• Communication and presentation skills</li>
              <li>• Project management abilities</li>
              <li>• Team collaboration</li>
              <li>• Industry networking</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Courses;